/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: moment.js library.
import _ from 'underscore' // Npm: underscore.js library.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { HiArrowPath } from 'react-icons/hi2' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Tab,
  TabList,
  Tabs,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'
import Modal from 'components/Modal'
import DownloadToExcel from "components/DownloadToExcel";
import PhoneNumberReplacementServiceUpsert from 'components/PhoneNumberReplacementServiceUpsert'
import PhoneNumberReplacementServiceDelete from 'components/PhoneNumberReplacementServiceDelete'
import PhoneNumberReplacementServiceDropDownOptions from 'components/PhoneNumberReplacementServiceDropDownOptions'


/*
 * GRAPHS
 */
import PhoneNumberReplacementServiceReadQuery from './__query__/index.phoneNumberReplacementService.read.query'


/*
 * STYLES
 */
import { buttonStyle, tabStyles } from './index.style'


/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _Customers = React.useMemo(() => Object.React.App.enums.CONTENT_TRANSLATION_TYPE.enums.map(i => i.key), [])
  const _successFlags = React.useMemo(() => Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key), [])

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [selectAll, setSelectAll] = React.useState(false)
  const [tabIndex, setTabIndex] = React.useState(0)
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [filterModel, setFilterModel] = React.useState({
    'items': [],
    'logicOperator': 'and',
    'quickFilterValues': [],
    'quickFilterLogicOperator': 'and'
  })
  const _setFilterDebounced = React.useMemo(() => _.debounce(setFilterModel, 500), [])
  const [selectedIds, setSelectedIds] = React.useState([])
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);
  const _rowCountRef = React.useRef(0);

  const _isFilterApplied = React.useMemo(
    () => filterModel?.items?.some((i) => i.value != void 0 && i.value !== ""),
    [filterModel],
  );
  const _appliedFilter = React.useMemo(
    () => (_isFilterApplied ? filterModel : void 0),
    [_isFilterApplied, filterModel],
  );
  const _phoneNumberReplacementServiceReadVariables = React.useMemo(
    () => ({
      type: _Customers[tabIndex],
      filter: _appliedFilter,
      skip: paginationModel.page * paginationModel.pageSize,
      take: paginationModel.pageSize,
    }),
    [
      _Customers,
      _appliedFilter,
      paginationModel.page,
      paginationModel.pageSize,
      tabIndex,
    ],
  );

  const _QueryPhoneNumberReplacementServiceRead = useQuery(
    PhoneNumberReplacementServiceReadQuery,
    {
      variables: _phoneNumberReplacementServiceReadVariables,
      fetchPolicy: _isPreviousPageRef.current
        ? "cache-first"
        : Object.React.App.fetchPolicy,
    },
  );

  const { refetch } = _QueryPhoneNumberReplacementServiceRead;
  const { 'onOpen': onPhoneNumberReplacementServiceOpen, 'isOpen': isPhoneNumberReplacementServiceOpen, 'onClose': onPhoneNumberReplacementServiceClose } = useDisclosure()
  const { 'isOpen': isPhoneNumberReplacementServiceUpdateOpen, 'onOpen': onPhoneNumberReplacementServiceUpdateOpen, 'onClose': onPhoneNumberReplacementServiceUpdateClose } = useDisclosure()
  const { 'isOpen': isPhoneNumberReplacementServiceDeleteOpen, 'onOpen': onPhoneNumberReplacementServiceDeleteOpen, 'onClose': onPhoneNumberReplacementServiceDeleteClose } = useDisclosure()
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  React.useEffect(() => {
    return () => _setFilterDebounced.cancel?.()
  }, [_setFilterDebounced])

  // Event handler.
  React.useEffect(() => {
    if (_QueryPhoneNumberReplacementServiceRead.data) {
      setSelectedIds([]);
      setSelectAll(false);
    }
  }, [_QueryPhoneNumberReplacementServiceRead.data]);
  const _HandleSelectAll = React.useCallback(() => {
    const _nextSelectAll = !selectAll;

    setSelectAll(_nextSelectAll);
    setSelectedIds(
      _nextSelectAll
        ? _QueryPhoneNumberReplacementServiceRead?.data?.PhoneNumberoReplacementRead?.map(
            (item) => item.id,
          ) || []
        : [],
    );
  }, [
    _QueryPhoneNumberReplacementServiceRead?.data?.PhoneNumberoReplacementRead,
    selectAll,
  ]);
  const _HandleCheckboxChange = React.useCallback(id => {
    setSelectedIds(i => i.includes(id) ? i.filter(selectedId => selectedId !== id) : [...i, id])
  }, [])
  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false;
    setPaginationModel((j) => {
      const nextPaginationModel = {
        ...j,
        page: 0,
      };

      _paginationModelRef.current = nextPaginationModel;

      return nextPaginationModel;
    });
  }, []);
  const _HandlePaginationModelChange = React.useCallback(
    (__paginationModel) => {
      _isPreviousPageRef.current =
        __paginationModel.page < _paginationModelRef.current.page &&
        __paginationModel.pageSize === _paginationModelRef.current.pageSize;

      _paginationModelRef.current = __paginationModel;
      setPaginationModel(__paginationModel);
    },
    [],
  );
  const _HandleFilterModelChange = React.useCallback(
    (__filterModel) => {
      _ResetPagination();
      _setFilterDebounced(__filterModel);
    },
    [_ResetPagination, _setFilterDebounced],
  );
  const _HandleTabChange = React.useCallback(
    (r) => {
      _ResetPagination();
      setTabIndex(r);
    },
    [_ResetPagination],
  );
  const _HandleColumnVisibilityModelChange = React.useCallback((newModel) => {
    setColumnVisibilityModel(newModel);
  }, []);
  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await _QueryPhoneNumberReplacementServiceRead.refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [_QueryPhoneNumberReplacementServiceRead]);
  const _phoneNumberReplacementServiceReadRowCount =
    _QueryPhoneNumberReplacementServiceRead?.data?.PhoneNumberoReplacementRead
      ?.length;
  const rowCount = React.useMemo(() => {
    if (
      undefined !== _phoneNumberReplacementServiceReadRowCount &&
      null !== _phoneNumberReplacementServiceReadRowCount
    )
      _rowCountRef.current = _phoneNumberReplacementServiceReadRowCount;

    return _rowCountRef.current;
  }, [_phoneNumberReplacementServiceReadRowCount]);
  const selectedIdsSet = React.useMemo(
    () => new Set(selectedIds),
    [selectedIds],
  );
  const _GetPhoneNumberReplacementServiceIds = React.useCallback(
    (rowId) => _.uniq([...selectedIds, rowId]),
    [selectedIds],
  );
  const slots = React.useMemo(
    () => ({
      loadingOverlay: () => (
        <TableSpinner
          isLoading={true}
          isEmpty={false}
          chopHeightFromHundredPercentage={
            _tableHeaderHeightRef?.current?.clientHeight
          }
        />
      ),
      noResultsOverlay: () => (
        <TableSpinner
          isLoading={false}
          isEmpty={true}
          chopHeightFromHundredPercentage={
            _tableHeaderHeightRef?.current?.clientHeight
          }
        />
      ),
      noRowsOverlay: () => (
        <TableSpinner
          isLoading={false}
          isEmpty={true}
          chopHeightFromHundredPercentage={
            _tableHeaderHeightRef?.current?.clientHeight
          }
        />
      ),
    }),
    [],
  );

  // Define columns
  const columns = React.useMemo(
    () => [
      {
        field: "checkbox",
        headerName: "",
        width: 60,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderHeader: () => (
          <Checkbox
            bg="gray.300"
            onChange={_HandleSelectAll}
            isChecked={selectAll}
          />
        ),
        renderCell: ({ row }) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Checkbox
              bg="gray.300"
              isChecked={selectedIdsSet.has(row.id)}
              onChange={() => _HandleCheckboxChange(row.id)}
            />
          </Box>
        ),
      },
      {
        field: "sno",
        headerName: "S No.",
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "id",
        headerName: "ID",
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "Account.displayName",
        headerName: "Account",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "Mcc.countryName",
        headerName: "Country MCC",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "PhoneNumberoDirectory.displayName",
        headerName: "Phone Directory",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "createdAt",
        headerName: "Created At",
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "action",
        headerName: "Actions",
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        width: 100,
        renderCell: ({ row }) => (
          <PhoneNumberReplacementServiceDropDownOptions
            phoneNumberReplacementServiceId={row?.id}
            onPhoneNumberReplacementServiceUpdate={
              onPhoneNumberReplacementServiceUpdateOpen
            }
            onPhoneNumberReplacementServiceDelete={
              onPhoneNumberReplacementServiceDeleteOpen
            }
            selectedIds={selectedIds}
            phoneNumberReplacementServiceIds={_GetPhoneNumberReplacementServiceIds(
              row?.id,
            )}
          />
        ),
      },
    ],
    [
      _GetPhoneNumberReplacementServiceIds,
      _HandleCheckboxChange,
      _HandleSelectAll,
      onPhoneNumberReplacementServiceDeleteOpen,
      onPhoneNumberReplacementServiceUpdateOpen,
      selectAll,
      selectedIds,
      selectedIdsSet,
    ],
  );

  // Define rows
  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryPhoneNumberReplacementServiceRead?.data?.PhoneNumberoReplacementRead?.map(
          (item, index) =>
            _successFlags.includes(item.status)
              ? {
                  id:
                    item?.id ??
                    `phone-number-replacement-service-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
                  sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                  "Account.displayName":
                    item?.CustomerAccount?.displayName ?? "-",
                  "Mcc.countryName":
                    item?.Mcc?.mcc >= 0
                      ? `${item?.Mcc?.countryName} (${item?.Mcc?.mcc})`
                      : "-",
                  "PhoneNumberoDirectory.displayName":
                    item?.PhoneNumberoDirectory?.displayName ?? "-",
                  createdAt: _.isEmpty(item.createdAt)
                    ? "-"
                    : Moment(item.createdAt).format("YYYY-MM-DD"),
                  updatedAt: _.isEmpty(item?.updatedAt)
                    ? "-"
                    : Moment(item?.updatedAt).format("YYYY-MM-DD"),
                }
              : null,
        ),
      ),
    [
      _QueryPhoneNumberReplacementServiceRead?.data
        ?.PhoneNumberoReplacementRead,
      _successFlags,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );

  // Return component.
  return (
    <>
      <Flex
        className="phoneNumberReplacementService base"
        minH="78vh"
        maxH="78vh"
        width="100%"
        overflow="hidden"
      >
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          height="100%"
          width="100%"
          overflow="hidden"
        >
          <Flex
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            bg="linear-Gradient(73deg,#390EF6 0%, #9244F4 100%)"
            borderTopLeftRadius="20px"
            borderTopRadius="20px"
            p={_isCurrentViewMobile ? "12px" : "22px"}
            flexShrink={0}
            minH="auto"
          >
            <Tooltip
              label="Create Phone Number Replacement Service."
              fontSize="sm"
            >
              <Button
                w="auto"
                onClick={onPhoneNumberReplacementServiceOpen}
                leftIcon={<IoMdAdd />}
                style={buttonStyle}
              >
                New PhoneNumber Replacement
              </Button>
            </Tooltip>
            <Flex gap={_isCurrentViewMobile ? "10px" : "12px"} flexShrink={0}>
              <Tooltip label="Refresh replacements." fontSize="sm">
                <Button
                  borderRadius="8px"
                  fontWeight="450"
                  minW="20px"
                  backgroundColor="purple.200"
                  color="purple"
                  _hover={{ bg: "purple.800", color: "white" }}
                  leftIcon={<HiArrowPath />}
                  isDisabled={isRefreshing}
                  onClick={_HandleRefresh}
                >
                  Refresh
                </Button>
              </Tooltip>
              <DownloadToExcel
                cellsData={
                  0 ===
                    _QueryPhoneNumberReplacementServiceRead?.data
                      ?.PhoneNumberoReplacementRead?.length ||
                  !_.every(
                    _.pluck(
                      _QueryPhoneNumberReplacementServiceRead?.data
                        ?.PhoneNumberoReplacementRead,
                      "status",
                    ),
                    (i) => _successFlags?.includes(i),
                  )
                    ? []
                    : _QueryPhoneNumberReplacementServiceRead?.data?.PhoneNumberoReplacementRead?.map(
                        (item, __index) => ({
                          "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                          Account: item?.CustomerAccount?.displayName ?? "-",
                          "Country MCC":
                            item?.Mcc?.mcc >= 0
                              ? `${item?.Mcc?.countryName} (${item?.Mcc?.mcc})`
                              : "-",

                          "Phone Directory":
                            item?.PhoneNumberoDirectory?.displayName ?? "-",
                          "Created At": _.isEmpty(item?.createdAt)
                            ? "-"
                            : Moment(item.createdAt).format("YYYY-MM-DD"),
                          "Updated At": _.isEmpty(item?.updatedAt)
                            ? "-"
                            : Moment(item.updatedAt).format("YYYY-MM-DD"),
                        }),
                      )
                }
                headersData={[
                  "S.No.",
                  "Account",
                  "Country MCC",
                  "Operator MNC",
                  "Phone Directory",
                  "Created At",
                  "Updated At",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>
          <Tabs
            variant="enclosed"
            onChange={_HandleTabChange}
            display="flex"
            flexDirection="column"
            align="center"
            w="100%"
            flex={1}
            bg="white"
            isFitted
            overflow="hidden"
            minH={0}
          >
            <TabList
              bg="linear-Gradient(73deg,#390EF6 0%, rgba(146, 68, 244, 1) 100%)"
              overflowX={_isCurrentViewMobile ? "auto" : void 0}
              flexShrink={0}
              minH="auto"
            >
              {_Customers.map((u) => (
                <Tab
                  color="white"
                  textDecoration="line-through"
                  _focus={{ boxShadow: "none" }}
                  _selected={{ textDecoration: "none" }}
                  style={{ ...tabStyles, border: "none", borderRadius: 0 }}
                  p="1rem"
                  key={u}
                >
                  {u}
                </Tab>
              ))}
            </TabList>
            <Flex
              flex={1}
              flexDirection="column"
              border="2px solid #3A0FF6"
              borderTop="none"
              borderBottomRadius="15px"
              overflow="hidden"
              minH={0}
              width="100%"
            >
              <DataGrid
                rows={rows}
                columns={columns}
                rowHeight={58}
                rowCount={rowCount}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={_HandlePaginationModelChange}
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={_HandleFilterModelChange}
                pageSizeOptions={[10, 30, 50, 100]}
                slots={slots}
                disableRowSelectionOnClick
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={
                  _HandleColumnVisibilityModelChange
                }
                loading={_QueryPhoneNumberReplacementServiceRead?.loading}
                disableColumnMenu={false}
                sx={{
                  "& .MuiDataGrid-main": {
                    border: "none",
                    borderRadius: 0,
                  },
                }}
              />
            </Flex>
          </Tabs>
        </Flex>
        <Modal
          size="3xl"
          title="Create Replacement "
          isOpen={isPhoneNumberReplacementServiceOpen}
          onClose={onPhoneNumberReplacementServiceClose}
          isCentered={false}
        >
          <PhoneNumberReplacementServiceUpsert
            isCreateOnly={true}
            isOpen={isPhoneNumberReplacementServiceOpen}
            onClose={onPhoneNumberReplacementServiceClose}
            refetch={refetch}
            type={_Customers[tabIndex]}
            isVendor={"TERMINATION" === _Customers[tabIndex]}
          />
        </Modal>
        <Modal
          size="3xl"
          title="Update  Replacement "
          isOpen={isPhoneNumberReplacementServiceUpdateOpen}
          onClose={onPhoneNumberReplacementServiceUpdateClose}
          isCentered={false}
        >
          <PhoneNumberReplacementServiceUpsert
            isCreateOnly={false}
            isOpen={isPhoneNumberReplacementServiceUpdateOpen}
            onClose={onPhoneNumberReplacementServiceUpdateClose}
            refetch={refetch}
            type={_Customers[tabIndex]}
            isVendor={"TERMINATION" === _Customers[tabIndex]}
          />
        </Modal>
        <Modal
          size="md"
          title="Delete PhoneNumber Replacement "
          isOpen={isPhoneNumberReplacementServiceDeleteOpen}
          onClose={onPhoneNumberReplacementServiceDeleteClose}
        >
          <PhoneNumberReplacementServiceDelete
            onClose={onPhoneNumberReplacementServiceDeleteClose}
            refetch={refetch}
          />
        </Modal>
      </Flex>
    </>
  );
}

/*
 * PROPTYPES
 */
Index.propTypes = {}


/*
 * EXPORT
 */
export default React.memo(Index);
